import { useEffect, useMemo, useState } from 'react'
import { MIN_PAGINATION_SIZE } from 'constants/queries'
import { useAccountsContext } from 'hooks/context/useAccountsContext'
import { useGetPaymentLimits } from 'hooks/queries/useGetPaymentLimits'
import { useSearchablePagination } from 'hooks/useSearchablePagination'
import type { LimitLevel, LimitType } from 'types/paymentLimits'
import type { PaymentLimitsFilterProps } from '../components/PaymentLimitsFilters'

interface UsePaymentLimitsFiltersOptions {
  level: LimitLevel
  customerId?: string
}

export const usePaymentLimitsFilters = ({ level, customerId }: UsePaymentLimitsFiltersOptions) => {
  const { currentAccount } = useAccountsContext()
  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    useSearchablePagination(MIN_PAGINATION_SIZE)

  const [currencyFilter, setCurrencyFilter] = useState<string>('')
  const [paymentMethodFilter, setPaymentMethodFilter] = useState<string>('')
  const [limitTypeFilter, setLimitTypeFilter] = useState<string>('')

  const {
    data: limitsData,
    isFetching,
    isError,
  } = useGetPaymentLimits({
    accountId: currentAccount?.reference,
    ...(customerId && { customerId }),
    pageNumber: page,
    pageSize: rowsPerPage,
    level,
    ...(currencyFilter && { currency: currencyFilter }),
    ...(paymentMethodFilter && { paymentMethod: paymentMethodFilter }),
    ...(limitTypeFilter && { limitType: limitTypeFilter as LimitType }),
  })

  useEffect(() => {
    handleChangePage(0)
  }, [currencyFilter, paymentMethodFilter, limitTypeFilter])

  const filterProps: PaymentLimitsFilterProps = useMemo(
    () => ({
      currencyFilter,
      paymentMethodFilter,
      limitTypeFilter,
      onCurrencyFilterChange: setCurrencyFilter,
      onPaymentMethodFilterChange: setPaymentMethodFilter,
      onLimitTypeFilterChange: setLimitTypeFilter,
    }),
    [currencyFilter, paymentMethodFilter, limitTypeFilter]
  )

  const limits = limitsData?.content ?? []
  const totalElements = limitsData?.totalElements ?? 0
  const hasLimits = limits.length > 0
  const hasActiveFilters = !!currencyFilter || !!paymentMethodFilter || !!limitTypeFilter

  return {
    filterProps,
    limits,
    totalElements,
    hasLimits,
    hasActiveFilters,
    isFetching,
    isError,
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
  }
}
